/**
 * A filiação declarada na ficha, procurada no cadastro — e confirmada por gente.
 *
 * `nome_pai` e `nome_mae` são texto desde sempre e nunca viraram parentesco: quem tem a mãe nomeada
 * no cadastro não tem mãe no grafo. Este painel faz a ponte, e faz do único jeito honesto: ele
 * PROPÕE. Nome de mãe não identifica ninguém — «Maria da Silva» casa com dezenas, e a mãe certa
 * pode nem estar cadastrada.
 *
 * Por isso a tela mostra, em cada linha, as três coisas que decidem: quantos homônimos existem, se
 * há endereço em comum, e o nome como ele está escrito na ficha. O botão diz «Confirmar», não
 * «Aplicar»: quem afirma é o operador, e a linha gravada registra que foi ele.
 */
import { AlertTriangle, Check, MapPin, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip, Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';

type Candidata = {
  papel: 'mae' | 'pai';
  nome_declarado: string;
  genitor_id: string;
  nome: string;
  data_nascimento: string | null;
  concorrentes: number;
  tipo: string;
  evidencia: { via: string; confianca: number; detalhe: Record<string, unknown> };
};

type Resposta = { candidatas: Candidata[]; limiar: number };

type PessoaRef = { id: string; nome_mae?: string | null; nome_pai?: string | null };

const PAPEL: Record<'mae' | 'pai', string> = { mae: 'Mãe', pai: 'Pai' };

/** Data pura não passa pelo `Date`: `new Date('1965-02-10')` é meia-noite UTC e exibe 09/02. */
function soData(iso: string): string {
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
}

function Linha({
  c,
  confirmando,
  onConfirmar,
}: {
  c: Candidata;
  confirmando: boolean;
  onConfirmar: (c: Candidata) => void;
}) {
  const comEndereco = c.evidencia.via === 'nome_e_endereco';
  return (
    <li className="flex items-start justify-between gap-3 border-b border-border py-3 last:border-0">
      <div className="min-w-0">
        <p className="tipo-corpo">
          <span className="text-text-tertiary">{PAPEL[c.papel]}: </span>
          {c.nome}
          {c.data_nascimento && (
            <span className="text-text-tertiary"> · {soData(c.data_nascimento)}</span>
          )}
        </p>
        <p className="tipo-corpo-pequeno text-text-tertiary">
          A ficha declara «{c.nome_declarado}»
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {comEndereco ? (
            <Chip className="gap-1 border-success/40 text-success">
              <MapPin className="size-3" /> endereço em comum
            </Chip>
          ) : (
            <Chip className="border-border text-text-secondary">só pelo nome</Chip>
          )}
          {/* A disputa pelo nome é o que impede a confirmação às cegas. Uma só não é certeza —
              é só a única que alguém cadastrou até agora. */}
          {c.concorrentes > 1 && (
            <Chip className="gap-1 border-warning/40 text-warning">
              <AlertTriangle className="size-3" /> {c.concorrentes} candidatas para este nome
            </Chip>
          )}
        </div>
      </div>
      <Button
        variant="secundario"
        size="sm"
        disabled={confirmando}
        onClick={() => onConfirmar(c)}
        title={`Registrar que esta pessoa é ${PAPEL[c.papel].toLowerCase()} desta ficha`}
      >
        {confirmando ? <Spinner /> : <Check />}
        Confirmar
      </Button>
    </li>
  );
}

export function PainelFiliacao({
  item,
  recarregar,
}: {
  item: PessoaRef;
  recarregar: () => Promise<void>;
}) {
  const [resposta, setResposta] = useState<Resposta | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [confirmando, setConfirmando] = useState<string | null>(null);

  const declara = !!(item.nome_mae?.trim() || item.nome_pai?.trim());

  const buscar = useCallback(async () => {
    if (!declara) {
      setCarregando(false);
      return;
    }
    setCarregando(true);
    const r = await api.get<Resposta>(`/cadastro/pessoas/${item.id}/filiacao`);
    setCarregando(false);
    if (r.ok) setResposta(r.dados);
    else toast.error(r.erro);
  }, [item.id, declara]);

  useEffect(() => {
    void buscar();
  }, [buscar]);

  async function confirmar(c: Candidata) {
    setConfirmando(c.genitor_id + c.papel);
    const r = await api.post(`/cadastro/pessoas/${item.id}/filiacao`, {
      genitor_id: c.genitor_id,
      papel: c.papel,
    });
    setConfirmando(null);
    if (!r.ok) {
      toast.error(r.erro);
      return;
    }
    toast.success(`${c.nome} registrada como ${PAPEL[c.papel].toLowerCase()} — recíproca inclusa.`);
    // A aresta nova muda as Relações Pessoais e o grafo da ficha; e a candidata confirmada sai
    // desta lista, porque o backend não propõe o que já existe.
    await Promise.all([buscar(), recarregar()]);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="size-4 text-accent" />
          Filiação declarada
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!declara ? (
          <p className="tipo-corpo-pequeno text-text-tertiary">
            A ficha não declara mãe nem pai. Preencha «nome da mãe» ou «nome do pai» nos Dados
            Pessoais — ou espere o enriquecimento por fontes públicas trazer o dado — e as
            candidatas aparecem aqui.
          </p>
        ) : carregando ? (
          <Spinner />
        ) : resposta && resposta.candidatas.length > 0 ? (
          <>
            <p className="tipo-corpo-pequeno mb-2 text-text-tertiary">
              Quem no cadastro pode ser a filiação declarada. Confirmar grava o parentesco e a
              recíproca — e fica registrado que foi você quem afirmou.
            </p>
            <ul>
              {resposta.candidatas.map((c) => (
                <Linha
                  key={c.genitor_id + c.papel}
                  c={c}
                  confirmando={confirmando === c.genitor_id + c.papel}
                  onConfirmar={(x) => void confirmar(x)}
                />
              ))}
            </ul>
          </>
        ) : (
          <p className="tipo-corpo-pequeno text-text-tertiary">
            A ficha declara a filiação, e ninguém no cadastro casa com o nome. Isso não é um erro: o
            genitor existe, só não foi cadastrado ainda. {/* O limiar usado viaja na resposta: uma
            lista vazia pode ser ausência de gente OU régua apertada, e a tela tem de poder dizer
            qual das duas. */}
            {resposta && (
              <span className="text-text-tertiary">
                {' '}
                (busca por nome com limiar {resposta.limiar})
              </span>
            )}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
